import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { type ToastT, useSonner } from 'sonner';

type SonnerToasterPosition =
  'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';

import { ToastDrainBar } from './toast-drain-bar';
import { bindSonnerToastDrainPause } from './use-toast-drain-pause';

export const APP_TOAST_DEFAULT_DURATION_MS = 10_000;

/** Matches Sonner 2.x: only toasts rendered in this position container get a `data-index`. */
export function filterSonnerToastsForPosition(
  toasts: ToastT[],
  position: SonnerToasterPosition,
): ToastT[] {
  return toasts.filter(
    (toast, index) => (!toast.position && index === 0) || toast.position === position,
  );
}

export function resolveToastDurationMs(toast: ToastT, defaultDurationMs: number): number | null {
  if (toast.type === 'loading' || toast.promise) {
    return null;
  }
  const raw = toast.duration ?? defaultDurationMs;
  if (raw === Infinity || !Number.isFinite(raw) || raw <= 0) {
    return null;
  }
  return raw;
}

function SonnerToastDrainItem({
  toast,
  index,
  defaultDurationMs,
}: {
  toast: ToastT;
  index: number;
  defaultDurationMs: number;
}) {
  const durationMs = resolveToastDurationMs(toast, defaultDurationMs)!;
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [paused, setPaused] = useState(false);
  const resetKey = `${toast.id}:${String(toast.title)}:${String(toast.description)}`;

  useLayoutEffect(() => {
    let toastEl: HTMLElement | null = null;
    let mountEl: HTMLElement | null = null;
    let raf = 0;
    let disposed = false;
    let unbindPause: (() => void) | null = null;

    const bind = (): boolean => {
      toastEl = document.querySelector<HTMLElement>(
        `[data-sonner-toast][data-index="${index}"][data-mounted="true"]`,
      );
      if (!toastEl) {
        return false;
      }

      unbindPause = bindSonnerToastDrainPause(toastEl, setPaused);

      mountEl = toastEl.querySelector<HTMLElement>('[data-sonner-toast-drain-host]');
      if (!mountEl) {
        mountEl = document.createElement('div');
        mountEl.dataset.sonnerToastDrainHost = '';
        mountEl.className = 'pointer-events-none absolute inset-x-0 bottom-0 z-[2]';
        toastEl.appendChild(mountEl);
      }
      setHost(mountEl);
      return true;
    };

    const tryBind = () => {
      if (disposed || bind()) {
        return;
      }
      raf = requestAnimationFrame(tryBind);
    };

    tryBind();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      unbindPause?.();
      mountEl?.remove();
      setHost(null);
      setPaused(false);
    };
  }, [index, toast.id]);

  if (!host) {
    return null;
  }

  return createPortal(
    <ToastDrainBar
      durationMs={durationMs}
      resetKey={resetKey}
      paused={paused}
      testId="sonner-toast-timer"
    />,
    host,
  );
}

export function SonnerToastDrains({
  defaultDurationMs = APP_TOAST_DEFAULT_DURATION_MS,
  position = 'bottom-right',
}: {
  defaultDurationMs?: number;
  /** Must match the `Toaster` `position` this drain sits beside. */
  position?: SonnerToasterPosition;
}) {
  const { toasts } = useSonner();
  const renderedToasts = filterSonnerToastsForPosition(toasts, position);

  return (
    <>
      {renderedToasts.map((toast, index) => {
        if (resolveToastDurationMs(toast, defaultDurationMs) === null) {
          return null;
        }
        return (
          <SonnerToastDrainItem
            key={toast.id}
            toast={toast}
            index={index}
            defaultDurationMs={defaultDurationMs}
          />
        );
      })}
    </>
  );
}
